// #1107: UI preferences use the shared chemistry palette, independently of artifact colors.
// npm run generate:themes keeps CSS and index.html's pre-paint allow-list in sync with it.

import { CHEMICAL_PALETTE, type ChemicalColorId } from '@peruselab/structure-editor/domain/chemicalPalette';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ThemeAccent = ChemicalColorId;

export interface ThemeAccentOption {
  readonly value: ThemeAccent;
  readonly name: string;
  readonly pigment: string;
  // The exact reference color; accessible UI tones are derived independently.
  readonly swatch: string;
}

export const DEFAULT_THEME_MODE: ThemeMode = 'system';
// Preserve the original preference keys; blue now identifies the reference Cobalt palette.
export const DEFAULT_THEME_ACCENT: ThemeAccent = 'blue';

// Order here is the order the switcher renders them in.
export const THEME_MODE_OPTIONS: readonly { readonly value: ThemeMode; readonly labelKey: string }[] = [
  { value: 'system', labelKey: 'workspace.account.theme.modes.system' },
  { value: 'light', labelKey: 'workspace.account.theme.modes.light' },
  { value: 'dark', labelKey: 'workspace.account.theme.modes.dark' }
];

const THEME_MODES: readonly ThemeMode[] = THEME_MODE_OPTIONS.map((option) => option.value);

// Order here is the order the switcher renders them in.
export const THEME_ACCENT_OPTIONS: readonly ThemeAccentOption[] = CHEMICAL_PALETTE.map((entry) => ({
  value: entry.id,
  name: entry.name,
  pigment: entry.pigment,
  swatch: entry.color
}));

const MODE_STORAGE_KEY = 'peruselab.theme.mode';
const ACCENT_STORAGE_KEY = 'peruselab.theme.accent';

function isThemeMode(value: string | null): value is ThemeMode {
  return value !== null && (THEME_MODES as readonly string[]).includes(value);
}

function isThemeAccent(value: string | null): value is ThemeAccent {
  return value !== null && THEME_ACCENT_OPTIONS.some((option) => option.value === value);
}

// localStorage can throw (private browsing, blocked storage, a full quota) -- never worth
// failing theme resolution over; every read/write here degrades to the default/no-op instead.

/** A missing key or an unrecognized value (e.g. an accent later removed from
 *  THEME_ACCENT_OPTIONS) both fall back to the default rather than erroring. */
export function readStoredThemeMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
  } catch {
    return DEFAULT_THEME_MODE;
  }
}

export function readStoredThemeAccent(): ThemeAccent {
  try {
    const stored = window.localStorage.getItem(ACCENT_STORAGE_KEY);
    return isThemeAccent(stored) ? stored : DEFAULT_THEME_ACCENT;
  } catch {
    return DEFAULT_THEME_ACCENT;
  }
}

export function storeThemeMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Best-effort -- see the note above.
  }
}

export function storeThemeAccent(accent: ThemeAccent): void {
  try {
    window.localStorage.setItem(ACCENT_STORAGE_KEY, accent);
  } catch {
    // Best-effort.
  }
}

/** Applies the preference to <html> as the data-theme-mode/data-theme-accent attributes
 *  styles.css keys off. "system" mode and the default "blue" accent both omit their attribute
 *  entirely; the generated CSS supplies the default Cobalt palette in both modes. */
export function applyThemePreference(mode: ThemeMode, accent: ThemeAccent): void {
  const root = document.documentElement;
  if (mode === 'system') delete root.dataset.themeMode;
  else root.dataset.themeMode = mode;

  if (accent === DEFAULT_THEME_ACCENT) delete root.dataset.themeAccent;
  else root.dataset.themeAccent = accent;
}
